import React, { useState } from 'react';
import { Heart, ShoppingCart, Truck, Star } from 'lucide-react';

// Single-file React component for a premium product page
// Tailwind CSS utility classes are used for styling. This component is self-contained
// and uses dummy data which you can replace with real props or API data.

export default function GreatHoneyProductPage() {
  const product = {
    id: 'uni-flower-raw-500',
    title: 'Uni-Flower Raw Honey — 500g',
    shortDescription:
      'Small-batch raw uni-flower honey — unfiltered, unheated, and harvested with care from sustainably managed hives.',
    price: 899, // INR
    oldPrice: 1099,
    rating: 4.9,
    reviewsCount: 214,
    image: '/images/uni-flower-honey-500.jpg', // replace with real image path
    gallery: [
      '/images/uni-flower-honey-500.jpg',
      '/images/hive-1.jpg',
      '/images/serving-1.jpg',
    ],
    availability: true,
    delivery: {
      nationwide: true,
      eta: '2-5 business days',
      cod: true,
    },
    beesUsed: ['Apis mellifera', 'Apis cerana (local strains)'],
    extractionLocation: 'KBR Park Apiary & Surrounding Farms, Hyderabad',
    segmentation: ['Raw', 'Uni-floral', 'Cold-extracted', 'Unfiltered'],
    longDescription:
      `Our Uni-Flower Raw Honey is slowly and gently harvested from single-flower blooms to preserve delicate aromatics and natural enzymes.

Harvested by expert beekeepers who follow strict sustainable practices, this honey is never heated or filtered, which means it retains trace pollen, phytonutrients, and that sublime floral character unique to each season. Perfect for drizzling, baking, or enjoying by the spoonful.`,
    whyBetter:
      `We partner directly with local farmers and beekeepers to ensure traceability and fair pay. Our hives are managed to prioritise bee health — no harmful treatments, rotational forage planning, and minimal hive disturbance. Small-batch production means every jar is inspected for taste, texture, and purity before it reaches you.`,
    howToUse:
      [`Take a spoonful straight for natural energy.`, `Stir into warm (not boiling) tea or milk.`, `Use as a glaze for roasted vegetables or meats.`, `Combine with yogurt, nuts, and fruit for a wholesome snack.`],
    reviews: [
      {
        id: 1,
        name: 'Anjali R.',
        rating: 5,
        text: 'Incredible aroma and texture — I can taste the uni-floral notes. Delivery was quick and packaging beautiful!',
      },
      {
        id: 2,
        name: 'Ravi K.',
        rating: 5,
        text: 'Finally a honey that tastes like honey. No aftertaste, purely sweet and natural.',
      },
    ],
    related: [
      { id: 'raw-250', title: 'Raw Multi-Flower — 250g', price: 399, image: '/images/multiflower-250.jpg' },
      { id: 'creamed-500', title: 'Creamed Honey — 500g', price: 749, image: '/images/creamed-500.jpg' },
      { id: 'flav-jar', title: 'Cinnamon Infused — 200g', price: 349, image: '/images/cinnamon-200.jpg' },
    ],
  };

  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [favourited, setFavourited] = useState(false);

  function addToCart() {
    const item = { id: product.id, title: product.title, price: product.price, qty: quantity };
    setCart((c) => [...c, item]);
    // in real app, call cart API or context
    // alert(${quantity} x ${product.title} added to cart.);
  }

  return (
    <div className="max-w-6xl mt-40 mx-auto p-6">
      {/* Top section: image left, short desc + buy area right */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        <div className="md:col-span-1">
          <div className="sticky top-6">
            <img
              src={product.image}
              alt={product.title}
              className="w-full h-72 object-cover rounded-2xl shadow-lg"
            />

            <div className="mt-4 grid grid-cols-3 gap-2">
              {product.gallery.map((g, i) => (
                <img
                  key={i}
                  src={g}
                  //alt={gallery-$i}
                  className="w-full h-20 object-cover rounded-lg border"
                />
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <h1 className="text-3xl font-extrabold tracking-tight">{product.title}</h1>

          <div className="mt-3 flex items-center gap-4">
            <div className="flex items-center gap-1 text-yellow-600">
              <Star size={18} /> <span className="font-semibold">{product.rating}</span>
            </div>
            <div className="text-sm text-slate-500">({product.reviewsCount} reviews)</div>
          </div>

          <p className="mt-4 text-lg text-slate-700">{product.shortDescription}</p>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:gap-6">
            <div className="flex items-baseline gap-3">
              <div className="text-2xl font-bold">₹{product.price}</div>
              <div className="text-sm line-through text-slate-400">₹{product.oldPrice}</div>
            </div>

            <div className="mt-3 sm:mt-0 flex items-center gap-2">
              <label className="text-sm text-slate-600">Qty</label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-20 p-2 border rounded-md"
              />
            </div>

            <div className="mt-3 sm:mt-0 flex items-center gap-3 ml-auto">
              <button
                onClick={addToCart}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg shadow"
              >
                <ShoppingCart size={16} /> Add to Cart
              </button>

              <button
                onClick={() => setFavourited((f) => !f)}
                className="inline-flex items-center gap-2 border rounded-lg px-3 py-2"
                aria-pressed={favourited}
              >
                <Heart size={16} className={favourited ? 'text-red-500' : ''} />
              </button>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-xs text-slate-500">Bees Used</div>
              <div className="font-medium mt-1">{product.beesUsed.join(', ')}</div>
            </div>

            <div className="p-4 border rounded-lg">
              <div className="text-xs text-slate-500">Harvest Location</div>
              <div className="font-medium mt-1">{product.extractionLocation}</div>
            </div>

            <div className="p-4 border rounded-lg">
              <div className="text-xs text-slate-500">Segmentation</div>
              <div className="font-medium mt-1">{product.segmentation.join(' • ')}</div>
            </div>
          </div>

          {/* Delivery & availability */}
          <div className="mt-6 flex items-center gap-4 p-4 bg-slate-50 rounded-lg border">
            <Truck size={18} />
            <div>
              {product.availability ? (
                <div className="text-sm font-medium">Available — Deliverable {product.delivery.eta}</div>
              ) : (
                <div className="text-sm font-medium text-rose-600">Currently unavailable</div>
              )}

              <div className="text-xs text-slate-500">COD: {product.delivery.cod ? 'Available' : 'Not available'}</div>
            </div>
          </div>

          {/* Long description, Why we're better, How to use */}
          <div className="mt-8 space-y-6">
            <section>
              <h3 className="text-lg font-semibold">About this Honey</h3>
              <p className="mt-2 text-slate-700 whitespace-pre-line">{product.longDescription}</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">Why We're Better</h3>
              <p className="mt-2 text-slate-700">{product.whyBetter}</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">How to Use</h3>
              <ul className="mt-2 list-disc pl-5 text-slate-700">
                {product.howToUse.map((u, i) => (
                  <li key={i}>{u}</li>
                ))}
              </ul>
            </section>
          </div>

          {/* Reviews */}
          <div className="mt-10">
            <h3 className="text-xl font-semibold">Customer Feedback</h3>
            <div className="mt-4 space-y-4">
              {product.reviews.map((r) => (
                <div key={r.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{r.name}</div>
                    <div className="flex items-center gap-1 text-yellow-600">{Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} size={14} />
                    ))}</div>
                  </div>
                  <p className="mt-2 text-slate-700">{r.text}</p>
                </div>
              ))}

              {/* Review form (simple) */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Leave a review</h4>
                <p className="text-sm text-slate-500 mt-1">Share your experience with this product.</p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <input className="flex-1 p-2 border rounded-md" placeholder="Your name" />
                  <input className="w-28 p-2 border rounded-md" placeholder="Rating (1-5)" />
                </div>
                <textarea className="w-full mt-3 p-2 border rounded-md" rows={3} placeholder="Your review" />
                <div className="mt-3">
                  <button className="px-4 py-2 bg-amber-500 text-white rounded-lg">Submit</button>
                </div>
              </div>
            </div>
          </div>

          {/* You may also like */}
          <div className="mt-10">
            <h3 className="text-xl font-semibold">You May Also Like</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {product.related.map((rp) => (
                <div key={rp.id} className="p-4 border rounded-lg flex gap-4 items-center">
                  <img src={rp.image} alt={rp.title} className="w-20 h-20 object-cover rounded" />
                  <div>
                    <div className="font-medium">{rp.title}</div>
                    <div className="text-sm text-slate-600">₹{rp.price}</div>
                    <div className="mt-2">
                      <button className="text-sm px-3 py-1 border rounded">View</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Simple cart summary at bottom */}
      <div className="fixed bottom-6 right-6 bg-white border rounded-full p-3 shadow-lg">
        <div className="flex items-center gap-3">
          <ShoppingCart size={18} />
          <div className="text-sm">{cart.length} item(s)</div>
        </div>
      </div>
    </div>
  );
}

